import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarCentavos } from '@/pages/loja/utils';

type Estado = 'carregando' | 'pronto' | 'sem-acesso' | 'erro';

interface ProdutoImpressao {
  produto_id: number;
  ponto_id: number;
  ponto_nome: string;
  nome: string;
  emoji: string | null;
  preco_centavos: number;
  controla_estoque: boolean;
  estoque_total: number | null;
}

interface Ficha {
  produto: string;
  pontoNome: string;
  emoji: string | null;
  preco_centavos: number;
  cor: string;
  numero: number;
}

// Uma cor de fundo por barraca (do sistema de tokens), para a barraca
// reconhecer a própria ficha de longe.
const CORES_POR_PONTO = [
  'bg-accent-100',
  'bg-primary-100',
  'bg-secondary-100',
  'bg-accent-200/60',
  'bg-primary-200/60',
  'bg-secondary-200/60',
  'bg-accent-100/70',
  'bg-primary-100/70',
];

export default function FichasImprimirPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const { eventoId } = useParams();
  const eventoIdNum = Number(eventoId);

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [eventoNome, setEventoNome] = useState('');
  const [produtos, setProdutos] = useState<ProdutoImpressao[]>([]);
  const [quantidades, setQuantidades] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        if (Number.isNaN(eventoIdNum)) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        const { data: eventoData, error: errEv } = await supabase
          .from('eventos')
          .select('nome, dono_id')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!eventoData) {
          if (ativo) setEstado('sem-acesso');
          return;
        }
        let permitido = (eventoData as { dono_id: string }).dono_id === user.id;
        if (!permitido) {
          const { data: equipe } = await supabase
            .from('equipe_evento')
            .select('id')
            .eq('evento_id', eventoIdNum)
            .eq('usuario_id', user.id)
            .eq('status', 'ativo')
            .maybeSingle();
          permitido = !!equipe;
        }
        if (!permitido) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        if (ativo) setEventoNome((eventoData as { nome: string }).nome);

        const { data: prods, error: errProds } = await supabase.rpc('loja_produtos_do_evento', {
          p_evento: eventoIdNum,
        });
        if (errProds) throw errProds;

        const ativos = ((prods as ProdutoImpressao[]) || []).filter((p) => p.ativo !== false);
        if (ativo) {
          setProdutos(ativos);
          const mapa = new Map<number, number>();
          ativos.forEach((p) => {
            const padrao =
              p.controla_estoque && p.estoque_total != null && p.estoque_total > 0
                ? p.estoque_total
                : 50;
            mapa.set(p.produto_id, padrao);
          });
          setQuantidades(mapa);
          setEstado('pronto');
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [navigate, eventoIdNum, pronto, user]);

  const corPorPonto = useMemo(() => {
    const ids = [...new Set(produtos.map((p) => p.ponto_id))];
    const mapa = new Map<number, string>();
    ids.forEach((id, i) => mapa.set(id, CORES_POR_PONTO[i % CORES_POR_PONTO.length]));
    return mapa;
  }, [produtos]);

  const fichas: Ficha[] = useMemo(() => {
    const lista: Ficha[] = [];
    produtos.forEach((p) => {
      const qtd = quantidades.get(p.produto_id) ?? 0;
      for (let i = 1; i <= qtd; i += 1) {
        lista.push({
          produto: p.nome,
          pontoNome: p.ponto_nome,
          emoji: p.emoji,
          preco_centavos: p.preco_centavos,
          cor: corPorPonto.get(p.ponto_id) ?? 'bg-secondary-100',
          numero: i,
        });
      }
    });
    return lista;
  }, [produtos, quantidades, corPorPonto]);

  const paginas = useMemo(() => {
    const chunks: Ficha[][] = [];
    for (let i = 0; i < fichas.length; i += 24) chunks.push(fichas.slice(i, i + 24));
    return chunks;
  }, [fichas]);

  function mudarQuantidade(produtoId: number, valor: number) {
    const v = Math.max(0, Math.floor(valor) || 0);
    setQuantidades((m) => {
      const novo = new Map(m);
      novo.set(produtoId, v);
      return novo;
    });
  }

  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro' || estado === 'sem-acesso') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">
            {estado === 'erro' ? 'Não foi possível carregar' : 'Sem acesso'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">{erro ?? 'Você não administra este evento.'}</p>
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${eventoId}/loja`)}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Voltar à loja
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          body { background: #fff !important; }
          .ficha-pagina { break-after: page; page-break-after: always; }
          .ficha-pagina:last-child { break-after: auto; page-break-after: auto; }
          .nao-imprimir { display: none !important; }
        }
      `}</style>

      <div className="nao-imprimir mx-auto w-full max-w-[960px] px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(`/gerenciar/${eventoId}/loja`)}
          className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          Voltar à loja
        </button>
        <h1 className="mt-2 text-2xl font-bold text-foreground-950">Fichas para imprimir</h1>
        <p className="text-sm text-secondary-500">
          {eventoNome} · 24 fichas por página · corte com tesoura.
        </p>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.print()}
            className="cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-printer-line" />
              </span>
              Imprimir
            </span>
          </button>
          <span className="text-sm text-secondary-500">
            {fichas.length} {fichas.length === 1 ? 'ficha' : 'fichas'} no total
          </span>
        </div>

        {/* Quantidades por produto */}
        <div className="mt-6 space-y-3">
          {produtos.length === 0 ? (
            <p className="text-sm text-secondary-500">Nenhum produto ativo na loja.</p>
          ) : (
            produtos.map((p) => (
              <div
                key={p.produto_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-secondary-200 bg-white px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-medium text-foreground-950">
                    {p.emoji} {p.nome}
                  </p>
                  <p className="text-xs text-secondary-500">
                    {p.ponto_nome} · {formatarCentavos(p.preco_centavos)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => mudarQuantidade(p.produto_id, (quantidades.get(p.produto_id) ?? 0) - 1)}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-secondary-300 text-foreground-800 transition-colors hover:bg-background-100"
                    aria-label="Menos"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={quantidades.get(p.produto_id) ?? 0}
                    onChange={(e) => mudarQuantidade(p.produto_id, Number(e.target.value))}
                    className="w-16 rounded-lg border border-secondary-300 bg-white px-2 py-2 text-center text-base font-bold text-foreground-950 outline-none focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => mudarQuantidade(p.produto_id, (quantidades.get(p.produto_id) ?? 0) + 1)}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-secondary-300 text-foreground-800 transition-colors hover:bg-background-100"
                    aria-label="Mais"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* As fichas (sempre visíveis, prontas para imprimir) */}
      <div className="mx-auto w-full max-w-[960px] px-4 py-6">
        {paginas.length === 0 ? (
          <p className="nao-imprimir text-center text-sm text-secondary-500">
            Ajuste as quantidades para gerar as fichas.
          </p>
        ) : (
          paginas.map((pagina, i) => (
            <div
              key={i}
              className="ficha-pagina mb-6 grid grid-cols-4 gap-2 rounded-lg border border-secondary-200 bg-white p-3"
            >
              {pagina.map((f, j) => (
                <div
                  key={j}
                  className={`flex aspect-[4/3] flex-col items-center justify-center rounded-md border border-secondary-300 px-1 text-center ${f.cor}`}
                >
                  <p className="text-[9px] font-semibold uppercase leading-tight tracking-wide text-secondary-600">
                    {eventoNome}
                  </p>
                  <span className="mt-1 text-2xl leading-none">{f.emoji || '🛍️'}</span>
                  <p className="mt-1 text-lg font-extrabold uppercase leading-tight text-foreground-950">
                    {f.produto}
                  </p>
                  <p className="mt-0.5 text-base font-extrabold text-primary-700">
                    {formatarCentavos(f.preco_centavos)}
                  </p>
                  <p className="mt-0.5 text-[9px] leading-tight text-secondary-600">
                    {f.pontoNome} · nº {String(f.numero).padStart(4, '0')}
                  </p>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}